import { MEDIA_STALE_TIME } from '@/utils/media';
import { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { queryKeys } from './query-keys';

const MAX_QUERY_FAILURES = 3;

/**
 * Default query retry policy. 4xx responses are deterministic — auth,
 * permission, and validation failures don't heal on retry, so retrying them
 * only multiplies error traffic (e.g. the 403 burst from an expired circle
 * JWT, EVE-5971). The exception is 429: rate limiting is transient, and React
 * Query's exponential backoff between retries gives it room to clear. Network
 * failures and 5xx responses keep React Query's default of three retries.
 */
export const shouldRetryQuery = (
    failureCount: number,
    error: unknown
): boolean => {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (status !== undefined && status >= 400 && status < 500) {
        return status === 429 && failureCount < MAX_QUERY_FAILURES;
    }
    return failureCount < MAX_QUERY_FAILURES;
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: { staleTime: 2000, retry: shouldRetryQuery },
    },
});

/**
 * cacheGuides/cacheProgramMetadata/cachePrograms/cachePacks write per-item
 * entries via setQueryData, which nothing observes until the item is used.
 * Unobserved, they'd otherwise fall to TanStack's 5-minute default gcTime and
 * be collected before their 15-minute staleTime says they're stale.
 */
queryClient.setQueryDefaults([queryKeys.guides], {
    gcTime: MEDIA_STALE_TIME,
});
queryClient.setQueryDefaults([queryKeys.programMetadatas], {
    gcTime: MEDIA_STALE_TIME,
});
queryClient.setQueryDefaults([queryKeys.programs], {
    gcTime: MEDIA_STALE_TIME,
});
queryClient.setQueryDefaults([queryKeys.packsByPackClass], {
    gcTime: MEDIA_STALE_TIME,
});

export default queryClient;
